/**
 * @file useOrderWorkspace.ts
 * @description Hook managing table ordering, item staging, and delta kitchen dispatching.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config/supabase.config';
import { fetchVenueMenu } from '../services/menuCatalog.service';
import { sendOrderDeltaToKitchen } from '../services/orderDeltaSender.service';
import { cancelOrderItem } from '../../modifications/services/orderModification.service';
import type { WaiterTable, StagedOrderItem, MenuItem, MenuItemModifier } from '../../../types/waiter.types';
import type { MenuCategory } from '../services/menuCatalog.service';

export const useOrderWorkspace = (
  venueId: string | null,
  activeTable: WaiterTable | null,
  waiterId: string | null,
  waiterName: string
) => {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [stagedItems, setStagedItems] = useState<StagedOrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 1. Load catalog
  useEffect(() => {
    if (!venueId) return;
    const loadCatalog = async () => {
      try {
        setIsLoading(true);
        const { categories: cat, items: itm } = await fetchVenueMenu(venueId);
        setCategories(cat);
        setMenuItems(itm);
      } catch (err: any) {
        setError(err?.message || 'Failed to load menu');
      } finally {
        setIsLoading(false);
      }
    };
    loadCatalog();
  }, [venueId]);

  // 2. Load existing order items for active table
  const loadExistingOrderItems = useCallback(async () => {
    if (!activeTable?.active_order_id) {
      setStagedItems([]);
      return;
    }

    try {
      const { data, error: err } = await supabase
        .from('order_items')
        .select('id, item_id, price, quantity, notes, status, course, selected_modifiers, items(name)')
        .eq('order_id', activeTable.active_order_id);

      if (err) throw err;

      const loaded: StagedOrderItem[] = (data || []).map((row: any) => ({
        clientTempId: row.id,
        existingOrderItemId: row.id,
        itemId: row.item_id,
        name: row.items?.name || 'Item',
        price: Number(row.price) || 0,
        quantity: row.quantity,
        notes: row.notes,
        course: (row.course as any) || 'main',
        modifiers: Array.isArray(row.selected_modifiers) ? row.selected_modifiers : [],
        isNewStaged: false, // Already sent to kitchen
        status: row.status,
      }));

      setStagedItems(loaded);
    } catch (err: any) {
      console.error('[useOrderWorkspace] Failed to load table items:', err);
    }
  }, [activeTable?.active_order_id]);

  useEffect(() => {
    loadExistingOrderItems();
  }, [loadExistingOrderItems]);

  // 3. Add item to staged tray (rapid ordering, no unnecessary confirm dialogs)
  const addStagedItem = (
    item: MenuItem,
    cookingStyle?: string | null,
    modifiers: MenuItemModifier[] = [],
    notes?: string | null,
    course: 'starter' | 'main' | 'dessert' | 'drinks' = 'main'
  ) => {
    const extraPrice = modifiers.reduce(
      (sum, m) => sum + (Number(m.total_price) || (Number(m.price) || 0) * (m.quantity || 1)),
      0
    );
    const finalPrice = item.price + extraPrice;

    const newItem: StagedOrderItem = {
      clientTempId: `staged-${Date.now()}-${Math.random().toString(36).substring(7)}`,
      itemId: item.id,
      name: item.name,
      price: finalPrice,
      quantity: 1,
      cookingStyle,
      modifiers,
      notes,
      course,
      isNewStaged: true, // Tagged as delta to be sent
      status: 'pending',
    };

    setStagedItems(prev => [...prev, newItem]);
  };

  // 4. Remove un-sent staged item or cancel sent item
  const removeItem = async (tempId: string, cancelReason?: string) => {
    const item = stagedItems.find(i => i.clientTempId === tempId);
    if (!item) return;

    if (item.isNewStaged) {
      // Not yet sent: simply remove from local draft
      setStagedItems(prev => prev.filter(i => i.clientTempId !== tempId));
    } else if (item.existingOrderItemId && cancelReason) {
      // Already sent to kitchen: execute audit-logged cancellation
      await cancelOrderItem(item.existingOrderItemId, cancelReason);
      await loadExistingOrderItems();
    }
  };

  // 5. Send only delta items to kitchen
  const sendToKitchen = async () => {
    if (!venueId || !activeTable) return;
    try {
      setIsSending(true);
      await sendOrderDeltaToKitchen({
        venueId,
        tableId: activeTable.id,
        tableNumber: activeTable.table_number,
        existingOrderId: activeTable.active_order_id || null,
        items: stagedItems,
        waiterId,
        waiterName,
        guestCount: activeTable.guest_count,
      });

      // Mark all items as sent
      setStagedItems(prev => prev.map(i => ({ ...i, isNewStaged: false })));
      await loadExistingOrderItems();
    } catch (err: any) {
      setError(err?.message || 'Failed to dispatch order to kitchen');
      throw err;
    } finally {
      setIsSending(false);
    }
  };

  const filteredMenuItems = selectedCategory === 'all'
    ? menuItems
    : menuItems.filter(i => i.category_id === selectedCategory);

  const subtotal = stagedItems
    .filter(i => i.status !== 'cancelled')
    .reduce((sum, i) => sum + i.price * i.quantity, 0);

  const newItemsCount = stagedItems.filter(i => i.isNewStaged).length;

  return {
    categories,
    menuItems: filteredMenuItems,
    selectedCategory,
    setSelectedCategory,
    stagedItems,
    subtotal,
    newItemsCount,
    isLoading,
    isSending,
    error,
    addStagedItem,
    removeItem,
    sendToKitchen,
  };
};
